/**
 * Cookie-era auth state helpers.
 *
 * The JWT lives in an httpOnly cookie, so JS cannot read it. We mirror the
 * last successful `auth.session()` result into memory. localStorage is only a
 * transitional fallback for Google OAuth paths that still write
 * SALESIVE_AUTH_MODE — and it must never override a server-known ghost/none.
 */

const mirror =
    (typeof window !== "undefined" && window.__salesiveSessionMirror) ||
    { active: false, user: false, serverMode: null };

if (typeof window !== "undefined") {
    window.__salesiveSessionMirror = mirror;
}

const localToken = () => {
    try {
        if (typeof localStorage === "undefined") return null;
        return localStorage.getItem("SALESIVE_ACCESS_TOKEN");
    } catch {
        return null;
    }
};

const localMode = () => {
    try {
        if (typeof localStorage === "undefined") return null;
        return localStorage.getItem("SALESIVE_AUTH_MODE");
    } catch {
        return null;
    }
};

/** Drop transitional localStorage auth keys so they cannot resurrect a session. */
export const clearLocalAuthArtifacts = () => {
    try {
        if (typeof localStorage === "undefined") return;
        localStorage.removeItem("SALESIVE_ACCESS_TOKEN");
        localStorage.removeItem("SALESIVE_AUTH_MODE");
    } catch {
        /* ignore quota / private mode */
    }
};

/**
 * Update the in-memory cookie-era session flags.
 * `serverMode` is "user" | "ghost" | "none" once we've heard from the API.
 */
export const applySessionMirror = ({ active, user, serverMode = null }) => {
    mirror.active = !!active;
    mirror.user = !!user;
    if (serverMode !== null && serverMode !== undefined) {
        mirror.serverMode = serverMode;
    }
};

/**
 * True when a session exists — either a cookie-backed session mirrored
 * into memory, or a legacy localStorage token.
 */
export const hasSession = () => {
    if (mirror.serverMode === "none") return false;
    if (mirror.active) return true;
    return !!localToken();
};

/**
 * True when the session belongs to a signed-in user (not a ghost).
 * Once the server has answered, that answer wins over localStorage.
 */
export const isLoggedIn = () => {
    if (mirror.serverMode === "user") return true;
    if (mirror.serverMode === "ghost" || mirror.serverMode === "none") {
        return false;
    }
    if (mirror.active) return !!mirror.user;
    const mode = localMode();
    const token = localToken();
    return !!token && mode === "USER";
};

/**
 * True when the current session is a ghost (anonymous).
 */
export const isGhost = () => {
    if (mirror.serverMode === "ghost") return true;
    if (mirror.serverMode === "user" || mirror.serverMode === "none") {
        return false;
    }
    if (mirror.active) return !mirror.user;
    return localMode() === "GHOST";
};

/** Current SALESIVE_AUTH_MODE value (for same-tab change detection). */
export const getLocalAuthMode = () => localMode();
